import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useGetMe, UserProfile } from '@workspace/api-client-react';
import { useLocation } from 'wouter';
import { apiUrl } from './api-url';

interface AuthContextType {
  token: string | null;
  user: UserProfile | null;
  isLoading: boolean;
  login: (token: string) => void;
  logout: (redirectTo?: string) => void;
  refreshUser: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = 'papacito_token';

function getInitialToken(): string | null {
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('token');
  if (urlToken) {
    localStorage.setItem(TOKEN_KEY, urlToken);
    const isNewOAuthUser = params.get('new') === '1';
    if (isNewOAuthUser) {
      import('@/lib/pixel').then(({ pixelLead }) => {
        pixelLead();
        console.log('[FB Pixel] Lead fired — OAuth new user');
      });
    }
    params.delete('token');
    params.delete('new');
    const remaining = params.toString();
    const cleanUrl = window.location.pathname + (remaining ? `?${remaining}` : '');
    window.history.replaceState({ papacito: true }, '', cleanUrl);
    window.history.pushState({ papacito: true }, '', cleanUrl);
    return urlToken;
  }
  return localStorage.getItem(TOKEN_KEY);
}

function scheduleTokenRefresh(token: string) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000;
    const refreshAt = exp - 7 * 24 * 60 * 60 * 1000;
    const delay = Math.max(refreshAt - Date.now(), 0);
    if (delay < 30 * 24 * 60 * 60 * 1000) {
      return window.setTimeout(async () => {
        try {
          const res = await fetch(apiUrl('/auth/refresh-token'), { method: 'POST' });
          if (res.ok) {
            const data = await res.json();
            if (data.token) {
              localStorage.setItem(TOKEN_KEY, data.token);
            }
          }
        } catch {}
      }, delay);
    }
  } catch {}
  return null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(getInitialToken);
  const [, setLocation] = useLocation();
  const refreshTimerRef = useRef<number | null>(null);

  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useGetMe({
    query: {
      queryKey: ['auth', 'me', token],
      enabled: !!token,
      retry: 2,
      retryDelay: 1000,
    },
  });

  useEffect(() => {
    if (token) {
      refreshTimerRef.current = scheduleTokenRefresh(token);
    }
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [token]);

  useEffect(() => {
    if (user && user.id && 'serviceWorker' in navigator) {
      const msg = { type: 'SET_USER_ID', userId: user.id };
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage(msg);
      } else {
        navigator.serviceWorker.ready.then((reg) => {
          reg.active?.postMessage(msg);
        }).catch(() => {});
      }
    }
  }, [user]);

  useEffect(() => {
    if (error) {
      const err = error as any;
      if (err?.status === 401 || err?.response?.status === 401) {
        logout();
      }
    }
  }, [error]);

  const login = useCallback((newToken: string) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
  }, []);

  const logout = useCallback(async (redirectTo?: string) => {
    const { resetAnalytics } = await import('@/lib/analytics');
    const { clearCsrfToken } = await import('@/lib/csrf');
    resetAnalytics();
    try {
      await fetch(apiUrl('/auth/logout'), { method: 'POST', credentials: 'include' });
    } catch {}
    clearCsrfToken();
    localStorage.removeItem(TOKEN_KEY);
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    setToken(null);
    setLocation(redirectTo || '/login');
  }, [setLocation]);

  return (
    <AuthContext.Provider
      value={{
        token,
        user: user || null,
        isLoading: !!token && isLoading,
        login,
        logout,
        refreshUser: refetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
